/**
 * AP-40 6.1 — **Bausteine der Bereichs-Dashboards.**
 *
 * Herausgelöst aus dem Projekt-Dashboard (AP-35 Blatt 8 5.1), damit alle Dashboards gleich
 * aussehen und gleich funktionieren:
 *
 * - `Kachel` — Zahl groß, Bezeichnung, darunter Hinweis oder Betrag; die ganze Kachel ist ein
 *   Verweis auf die gefilterte Liste (6.4). Fehlt der Baustein, steht sie grau mit Erklärung;
 *   fehlt das Recht, kommt sie vom Server gar nicht erst (MD-7) und erscheint nicht.
 * - `KachelRaster`, `DashboardListe` (höchstens 8 Zeilen, dann „alle"), `DashboardKopf` mit dem
 *   Umschalter „alle / meine" und „Aktualisiert vor …", `useDashboard` (Laden, gespeicherte
 *   Wahl je Bereich, AP-23).
 *
 * AP-18 5.3: Unter dem Kopf steht das Eingabefeld „Frag den Assistenten …" — im Kopf selbst
 * eingebaut, damit jedes Bereichs-Dashboard es hat, auch ein künftiges.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { LifeBuoy, type LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { dashboardEinstellung, type DashboardAntwort, type DashboardScope, type DashboardTile } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { formCard } from '@/lib/form-styles'
import { moduleLabel } from '@/lib/module-label'
import { loadUiSetting, saveUiSetting } from '@/lib/ui-settings'
import { hilfeSichtbar, useHilfe } from '@/lib/hilfe'
import { AssistentFrage } from '@/components/assistent/AssistentFrage'

export type KachelTon = 'normal' | 'warnung' | 'gefahr'

const TON: Record<KachelTon, string> = {
  normal: '',
  warnung: 'text-amber-700 dark:text-amber-300',
  gefahr: 'text-destructive',
}

/**
 * Eine Kachel. `kachel` fehlt → nichts (kein Recht). Nicht verfügbar → grau mit dem Namen des
 * Bausteins. Sonst Zahl, Hinweis und Verweis.
 */
export function Kachel({
  titel,
  kachel,
  hinweis,
  ton = 'normal',
  zu,
  wert,
}: {
  titel: string
  kachel: DashboardTile | undefined
  hinweis?: ReactNode
  /** Farbe der Zahl — rot für Überfälliges, nur wenn die Zahl nicht 0 ist. */
  ton?: KachelTon
  /** Ziel, wenn es nicht schon in der Kachel steht (`href`). */
  zu?: string
  /** Statt der Zahl anzeigen — etwa Stunden im Format des Mandanten. */
  wert?: string
}) {
  const { t } = useTranslation()
  if (!kachel) return null
  if (!kachel.available) {
    const name = kachel.module ? moduleLabel(kachel.module, t) : ''
    return (
      <div className={`${formCard} text-sm text-muted-foreground`} data-testid="kachel-grau">
        <p className="text-xs uppercase tracking-wide">{titel}</p>
        <p className="mt-1 text-xs">
          {name ? `Der Baustein „${name}" ist nicht gebucht.` : 'Nicht gebucht.'} Unter „Einstellungen → Module" steht, wie sich das ändern lässt.
        </p>
      </div>
    )
  }
  const ziel = zu ?? kachel.href
  const inhalt = (
    <>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{titel}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${kachel.count ? TON[ton] : ''}`}>{wert ?? kachel.count.toLocaleString('de-DE')}</p>
      {hinweis && <p className="mt-0.5 text-xs text-muted-foreground">{hinweis}</p>}
    </>
  )
  return ziel ? (
    <Link to={ziel} className={`${formCard} block text-left transition-colors hover:border-primary`}>
      {inhalt}
    </Link>
  ) : (
    <div className={`${formCard} text-left`}>{inhalt}</div>
  )
}

/** Einspaltig am Telefon, sonst so breit, wie Platz ist. */
export function KachelRaster({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
}

/** Eine kurze Liste — höchstens 8 Zeilen, darunter „alle". */
export function DashboardListe<T>({
  titel,
  zeilen,
  zeile,
  schluessel,
  alle,
  leer = 'Nichts offen.',
}: {
  titel: string
  zeilen: T[] | undefined
  zeile: (z: T) => ReactNode
  schluessel: (z: T) => string
  alle?: string
  leer?: string
}) {
  if (!zeilen) return null
  return (
    <div className={formCard}>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium">{titel}</h2>
        {alle && (
          <Link to={alle} className="text-xs text-primary hover:underline">
            alle
          </Link>
        )}
      </div>
      <ul className="space-y-1 text-sm">
        {zeilen.slice(0, 8).map((z) => (
          <li key={schluessel(z)} className="flex min-w-0 items-center gap-2">
            {zeile(z)}
          </li>
        ))}
        {zeilen.length === 0 && <li className="text-xs text-muted-foreground">{leer}</li>}
      </ul>
    </div>
  )
}

/** „vor 12 s", „vor 3 min" — für den Kopf. */
export function vorZeit(stand: string | undefined, jetzt: number): string {
  if (!stand) return ''
  const s = Math.max(0, Math.round((jetzt - new Date(stand).getTime()) / 1000))
  return s < 60 ? `vor ${s} s` : `vor ${Math.floor(s / 60)} min`
}

/**
 * Kopf: Symbol und Name des Bereichs, rechts der Umschalter und wann gerechnet wurde.
 * Das Symbol ist dasselbe wie am Hauptmenüpunkt (AppLayout `NAV_ITEMS`) — Pflicht, damit
 * kein Dashboard ohne auskommt.
 */
export function DashboardKopf({
  symbol: Symbol,
  titel,
  untertitel,
  bereich,
  onBereich,
  umschalter,
  beschriftung,
  stand,
  aktion,
  hilfe,
  assistent = true,
}: {
  symbol: LucideIcon
  titel: string
  untertitel?: string
  bereich: DashboardScope
  onBereich: (b: DashboardScope) => void
  umschalter: boolean
  /** Beschriftung der beiden Knöpfe, Vorgabe „alle" / „meine". */
  beschriftung?: Record<DashboardScope, string>
  stand?: string
  /** Ein Knopf oben rechts, etwa „Jetzt buchen". */
  aktion?: ReactNode
  /**
   * AP-18 Nachtrag 1 (6.3) — Ordner des Handbuch-Bereichs (`zeiterfassung`). Steht der Bereich im
   * Inhaltsverzeichnis des Benutzers, erscheint rechts „Hilfe zu …".
   */
  hilfe?: string
  /**
   * AP-18 5.3 — das Eingabefeld des Assistenten unter dem Kopf. `false` nur, wo der Assistent für den
   * Bereich nichts hat und nichts bekommen soll (Lohn, AP-18 15.3-7).
   */
  assistent?: boolean
}) {
  const { t: tr } = useTranslation()
  const hilfeInhalt = useHilfe((s) => s.inhalt)
  const hilfeBereich = hilfe && hilfeSichtbar(hilfeInhalt, hilfe) ? hilfeInhalt!.find((b) => b.area === hilfe) : undefined
  const [jetzt, setJetzt] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setJetzt(Date.now()), 10_000)
    return () => clearInterval(t)
  }, [])
  const text = beschriftung ?? { all: 'alle', mine: 'meine' }
  return (
    <>
    <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
      <div>
        <h1 className="flex items-center gap-2 text-3xl font-bold text-foreground">
          <Symbol className="h-7 w-7" aria-hidden /> {titel}
        </h1>
        {untertitel && <p className="mt-1 text-sm text-muted-foreground">{untertitel}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {stand && <span className="text-xs text-muted-foreground">Aktualisiert {vorZeit(stand, jetzt)}</span>}
        {umschalter && (
          <div className="flex items-center gap-1 rounded-md border border-border p-0.5 text-sm" role="group" aria-label="Umfang">
            {(['all', 'mine'] as const).map((wert) => (
              <button
                key={wert}
                type="button"
                aria-pressed={bereich === wert}
                onClick={() => onBereich(wert)}
                className={`rounded px-3 py-1 ${bereich === wert ? 'bg-primary text-primary-foreground' : ''}`}
              >
                {text[wert]}
              </button>
            ))}
          </div>
        )}
        {hilfeBereich && (
          <Link to={`/hilfe/${hilfeBereich.area}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
            <LifeBuoy className="h-4 w-4" aria-hidden /> {tr('help.dashboardLink', { area: hilfeBereich.title })}
          </Link>
        )}
        {aktion}
      </div>
    </div>
    {assistent && <AssistentFrage />}
    </>
  )
}

/** „Hier ist gerade nichts offen." mit den Verweisen auf die Unterseiten. */
export function LeerHinweis({ verweise }: { verweise: { zu: string; text: string }[] }) {
  return (
    <div className={`${formCard} text-sm text-muted-foreground`}>
      <p>Hier ist gerade nichts offen.</p>
      {verweise.length > 0 && (
        <p className="mt-1 flex flex-wrap gap-3">
          {verweise.map((v) => (
            <Link key={v.zu} to={v.zu} className="text-primary hover:underline">
              {v.text}
            </Link>
          ))}
        </p>
      )}
    </div>
  )
}

/**
 * Lädt ein Dashboard. Die Wahl „alle / meine" bleibt je Benutzer und Bereich gespeicherte
 * Einstellung (AP-23, `dashboard_<bereich>`, MD-10).
 */
export function useDashboard<A extends DashboardAntwort>(bereichsSchluessel: string, laden: (scope: DashboardScope) => Promise<A>) {
  const einstellung = dashboardEinstellung(bereichsSchluessel)
  const [bereich, setBereichState] = useState<DashboardScope>(() => {
    const gespeichert = loadUiSetting<{ scope?: DashboardScope } | null>(einstellung, null)
    return gespeichert?.scope === 'mine' ? 'mine' : 'all'
  })
  const [daten, setDaten] = useState<A | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const neu = useCallback(async () => {
    try {
      setDaten(await laden(bereich))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Dashboard konnte nicht geladen werden.'))
    }
    // `laden` ist je Seite eine feste Funktion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bereich])

  useEffect(() => {
    void neu()
  }, [neu])

  const setBereich = (b: DashboardScope) => {
    setBereichState(b)
    saveUiSetting(einstellung, { scope: b })
  }

  return { bereich, setBereich, daten, fehler, neu, kachel: (key: string) => daten?.tiles?.[key] }
}
